import { useRoute, useRouter } from 'vue2-helpers/vue-router';
import { ROUTER, SPECIFIC_ROUTE } from '@/models/constants';

import RoutingComposable from '@dh-io-otb/shared/src/models/routing/routing.composable.model';

import { useStore } from '@dh-io-otb/shared/src/composables/store.composable';
import { useRouting } from '@dh-io-otb/shared/src/composables/routing.composable';

import RoutingService from '@/services/routing.service';
import UtilsService from '@dh-io-otb/shared/src/services/utils.service';
import {
  TRACKING_EVENT_ACTION,
  TRACKING_EVENT_CATEGORY,
  TRACKING_EVENT_NAME,
} from '@dh-io-otb/shared/src/models/constants';
import TrackingService from '@dh-io-otb/shared/src/services/tracking.service';
import { EventTrackingInfo } from '@dh-io-otb/shared/src/models/entities/tracking.model';

const useRoutingApplication = (): RoutingComposable => {
  const route = useRoute();
  const router = useRouter();
  const { state, commit, dispatch, getter } = useStore();

  const { isPreviousAvailable, isNextAvailable, metaParams, path, queryParams, clearQueryParam, previous } =
    useRouting();

  const deeplinkRedirect = () => {
    // If there's an error message, redirect to MESSAGE.
    if (state.otbcFlow.errorMessage) {
      nextSpecificRoute(SPECIFIC_ROUTE.MESSAGE);
      return;
    }

    switch (path?.value) {
      case ROUTER.REQUEST:
        handleRequestPath();
        break;
      case ROUTER.SCHEDULE:
        handleSchedulePath();
        break;
      default:
        handleDefaultPath();
        break;
    }
  };

  const handleRequestPath = () => {
    if (!state.otbcFlow.modelSelected || (!state.otbcFlow.didBookingSearch && !state.otbcFlow.isTestDriveFromHome)) {
      if (state.otbcFlow.fixedDealer) {
        nextSpecificRoute(SPECIFIC_ROUTE.SCHEDULE);
      } else if (state.configuration.bookingActive) {
        redirectToVMOS();
      }
    }
    if (!state.otbcFlow.locationSelected && !state.otbcFlow.fixedDealer) {
      nextSpecificRoute(SPECIFIC_ROUTE.DEFINE_LOCATION);
    }
  };

  const handleDefaultPath = () => {
    if (state.otbcFlow.fixedDealer) {
      if (!state.otbcFlow.didBookingSearch) {
        nextSpecificRoute(SPECIFIC_ROUTE.SCHEDULE);
        return;
      }
      if (path?.value === ROUTER.DEFINE_LOCATION) {
        nextSpecificRoute(SPECIFIC_ROUTE.SCHEDULE);
        return;
      }
    }
    if (!state.otbcFlow.modelSelected) {
      redirectToVMOS();
    } else if (!state.otbcFlow.locationSelected && !state.otbcFlow.fixedDealer) {
      nextSpecificRoute(SPECIFIC_ROUTE.DEFINE_LOCATION);
    } else if (!state.otbcFlow.preferencesSelected && state.configuration.bookingActive) {
      nextSpecificRoute(SPECIFIC_ROUTE.SCHEDULE);
    }
  };

  const handleSchedulePath = () => {
    if (!state.configuration.bookingActive && state.otbcFlow.locationSelected) {
      nextSpecificRoute(SPECIFIC_ROUTE.REQUEST);
    }
    if (!state.otbcFlow.locationSelected && !state.otbcFlow.fixedDealer) {
      nextSpecificRoute(SPECIFIC_ROUTE.DEFINE_LOCATION);
    }
  };

  const deselectLocation = () => {
    commit('locationSelected', false);
    commit('locationQueryFreeText', '');
    commit('requestLocationQueryFreeText', '');
    commit('bookingSearchFinished', false);
    commit('didBookingSearch', false);
    commit('initialLocation', {
      label: '',
      locationId: '',
      postalCode: '',
    });
    if (previous) previous();
  };

  const deselectPreferences = () => {
    commit('preferencesSelected', false);
    if (previous) previous();
  };

  const checkMultipleDealers = () => {
    if (!queryParams?.value) return false;

    const dealer = queryParams?.value?.dealer;
    const dealers = queryParams?.value?.dealers?.split(/,|;/).filter((d) => d !== '') ?? [];

    // if it has dealer param or multiple dealers do nothing
    if (dealer || dealers.length > 1) return false;

    // from this point onwards we are dealing with a list of dealers with a single dealer
    // so we are forcing the fixedDealer to be the only dealer in the list

    commit('didBookingSearch', false);

    const newQueryParams = { ...route.query, fixedDealer: 'true', dealer: dealers[0] };
    delete newQueryParams['dealers'];

    router.replace({ query: { ...newQueryParams } });
    // hack to force the vue router to reload the page due to NavigationDuplicated error
    // even though there are different query params
    router.go(0);

    return true;
  };

  const removeFixedDealer = () => {
    // do not remove fixed dealer if there are multiple dealers
    if (checkMultipleDealers()) return;

    commit('didBookingSearch', false);
    commit('fixedDealer', '');

    const newQueryParams = { ...route.query };
    delete newQueryParams['fixedDealer'];
    delete newQueryParams['referrer'];
    delete newQueryParams['dealer'];

    const currentRoute = router.currentRoute.fullPath;

    if (
      currentRoute.includes('fixedDealer=') ||
      currentRoute.includes('referrer=') ||
      currentRoute.includes('dealer=')
    ) {
      router.replace({ query: { ...newQueryParams } });
      // hack to force the vue router to reload the page due to NavigationDuplicated error
      // even though there are different query params
      router.go(0);
    }
  };

  const checkDealerParameters = () => {
    if (
      state.otbcFlow.dealer &&
      state.otbcFlow.dealer.length > 1 &&
      (state.otbcFlow.dealer.split(',') ?? []).length > 1
    ) {
      const newQueryParams = { ...route.query };
      newQueryParams['dealers'] = state.otbcFlow.dealer;

      delete newQueryParams['fixedDealer'];
      delete newQueryParams['referrer'];
      delete newQueryParams['dealer'];

      router.replace({ query: { ...newQueryParams } });
      // hack to force the vue router to reload the page due to NavigationDuplicated error
      // even though there are different query params
      router.go(0);
    }
  };

  const next = () => {
    if (path?.value === ROUTER.REQUEST || path?.value === ROUTER.PERSONAL_DATA) {
      if (getter.oneDocActive) {
        nextSpecificRoute(SPECIFIC_ROUTE.ONEDOC);
      } else {
        nextSpecificRoute(SPECIFIC_ROUTE.SUMMARY);
      }
    } else {
      if (isNextAvailable?.value) {
        router.push({ path: metaParams?.value?.next, query: queryParams?.value });
      } else {
        console.warn('Next route is not available!');
      }
    }
  };

  const nextSpecificRoute = (route: string) => {
    if (route === SPECIFIC_ROUTE.VMOS) {
      redirectToVMOS();
    } else if ((ROUTER as any)[route]) {
      // TODO: This catch exists because the [route.push] throws an error if the
      // specific route is already loaded in the page. In the future, we may implement
      // this catch and get the edge cases in order to reduce these specific cases.
      // Note: It happers basically because we do redirections based
      //////// on queryParams and sometimes the route is already loaded.
      router.push({ path: (ROUTER as any)[route], query: queryParams?.value }).catch(() => '');
    }
  };

  const redirectToVMOS = () => {
    const currentUrl = window.location.href;
    // The second condition assesses if the application is being rendered in the AEM Editor
    // and prevents it from redirecting to VMOS
    if (state.configuration.isStandalone || currentUrl.indexOf('aem.oneweb.mercedes-benz.com/content/') > -1) {
      nextSpecificRoute(SPECIFIC_ROUTE.DEEPLINK_LIST);
    } else {
      window.location.href = RoutingService.defineRedirectUrl(state.otbcFlow, state.configuration);
    }
  };

  const onMountedActions = async () => {
    await router.isReady();
    if (queryParams) RoutingService.setQueryParamsIntoStore(queryParams);
    deeplinkRedirect();
    UtilsService.getTopMostAllowedFrame().scrollTo(0, 0);
  };

  const selectModel = () => {
    commit('modelSelected', true);
    next();
  };

  const selectLocation = async () => {
    commit('locationSelected', true);
    commit('selectedModelMotorisations', state.otbcFlow.model ? state.otbcFlow.model.motorisations : '');
    commit('didBookingSearch', false);
    trackSelectLocation(TRACKING_EVENT_ACTION.SEARCH_EXECUTED, TRACKING_EVENT_NAME.EXECUTE_SEARCH);
    if (getter?.isTestDriveFromHomeEnabled) {
      nextSpecificRoute(SPECIFIC_ROUTE.REQUEST);
      commit('skippedResultsPage', true);
      return;
    }

    if (!state.configuration.bookingActive && state.otbcFlow.locationSelected) {
      nextSpecificRoute(SPECIFIC_ROUTE.REQUEST);
      return;
    }
    next();
    await dispatch('getTestDriveBookingVehicleSearchWithinRadius');

    if (state.otbcFlow.errorMessage) {
      deeplinkRedirect();
      return;
    }
    checkSkipResultsPage();
  };

  const formatRadius = (radius: string) => {
    const radiusParts = radius?.match(/\d+|\D+/g);
    return radiusParts ? `${radiusParts[0]}_${radiusParts[1]}` : radius;
  };

  const trackSelectLocation = (action: TRACKING_EVENT_ACTION, name?: TRACKING_EVENT_NAME) => {
    const datePickerStatus = state.otbcFlow.searchedDate ? 'date_changed' : 'date_unchanged';
    const label = `${state.otbcFlow.initialLocation?.label}|${formatRadius(
      state.otbcFlow.locationRadius,
    )}|${datePickerStatus}`;
    const event: EventTrackingInfo = {
      category: TRACKING_EVENT_CATEGORY.SEARCH,
      name,
      label,
      action,
    };
    TrackingService.trackEvent(event);
  };

  const checkSkipResultsPage = () => {
    if (!state.otbcFlow.didBookingSearch) return;
    if (!state.otbcFlow.bookingSearchFinished) return;
    if (!state.otbcFlow.skippedResultsPage) return;
    if (
      (!state.otbcFlow.bookingsSearchResults.length && state.configuration.shouldSkipNoResultsPage) ||
      !state.configuration.bookingActive ||
      getter.isTestDriveFromHomeEnabled
    ) {
      nextSpecificRoute(SPECIFIC_ROUTE.REQUEST);
      commit('skippedResultsPage', true);
    } else {
      commit('skippedResultsPage', false);
    }
  };

  const selectPreferences = () => {
    commit('preferencesSelected', true);
    next();
  };

  return {
    isPreviousAvailable,
    isNextAvailable,
    metaParams,
    path,
    queryParams,
    clearQueryParam,
    deselectLocation,
    deselectPreferences,
    next,
    nextSpecificRoute,
    onMountedActions,
    previous,
    selectModel,
    selectLocation,
    selectPreferences,
    removeFixedDealer,
    checkDealerParameters,
    redirectToVMOS,
    deeplinkRedirect,
    checkSkipResultsPage,
  };
};

export { useRoutingApplication };
